import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const reviews = [
  {
    id: 1,
    name: "James Carine",
    initial: "J",
    rating: 5,
    text: "Very polite and extremely professional. I was very pleased with the job they did cleaning my windows at a very fair price. Highly recommend!",
    color: "bg-teal-600",
  },
  {
    id: 2,
    name: "Sandra Liveric",
    initial: "S",
    rating: 5,
    text: "Knowledgable. Fair. Thorough. Reliable. The best! Highly recommend.",
    color: "bg-indigo-600",
  },
  {
    id: 3,
    name: "Sammy Benalloul",
    initial: "S",
    rating: 5,
    text: "Excellent service prompt and professional",
    color: "bg-indigo-600",
  },
  {
    id: 4,
    name: "Joshua Rodriguez",
    initial: "J",
    rating: 5,
    text: "I don't write reviews often, but I feel legally and morally obligated to share what happened after these fine gentlemen cleaned my windows. They were so unbelievably spotless, so crystal clear, that I walked straight through my sliding glass door. Full speed. None of this is true, of course. They just did THAT much of a stellar job. All jokes aside, these guys are absolutely, unequivocally INCREDIBLE. Prompt, professional, reasonably priced, and apparently capable of making glass functionally invisible. Long Island, Brooklyn, Manhattan, Queens, wherever you are in this big, beautiful city, stop scrolling and book these gloriously talented gentlemen of the squeegee. 10/10. Would shatter again.",
    color: "bg-green-700",
  },
];

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {Array(5).fill(0).map((_, i) => (
      <Star
        key={i}
        size={18}
        fill={i < rating ? "#F59E0B" : "#E5E7EB"}
        color={i < rating ? "#F59E0B" : "#E5E7EB"}
      />
    ))}
  </div>
);

const TestimonialCard = ({ review }) => (
  <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col h-full" data-testid={`review-card-${review.id}`}>
    <div className="flex items-center gap-4 mb-4">
      <div className={`${review.color} w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
        {review.initial}
      </div>
      <div>
        <h4 className="font-bold text-gray-900">{review.name}</h4>
        <StarRating rating={review.rating} />
      </div>
    </div>
    <p className="text-gray-700 leading-relaxed flex-grow">{review.text}</p>
    <div className="flex items-center gap-1.5 mt-4 pt-4 border-t border-gray-100">
      <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      <span className="text-xs text-gray-500">Google Review</span>
    </div>
  </div>
);

const Testimonials = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const reviewsPerPage = 3;
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const displayedReviews = reviews.slice(currentPage * reviewsPerPage, (currentPage + 1) * reviewsPerPage);

  return (
    <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            What Our Customers Say
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6">
            <div className="flex items-center gap-2">
              <StarRating rating={5} />
              <span className="text-2xl font-bold text-gray-900">5.0/5</span>
            </div>
            <div className="text-gray-600">
              Based on {reviews.length} Google Reviews
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedReviews.map((review) => (
            <TestimonialCard key={review.id} review={review} />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-30"
              data-testid="reviews-prev"
            >
              <ChevronLeft size={24} className="text-gray-700" />
            </button>
            <span className="text-sm text-gray-600">{currentPage + 1} / {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage === totalPages - 1}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-30"
              data-testid="reviews-next"
            >
              <ChevronRight size={24} className="text-gray-700" />
            </button>
          </div>
        )}

        {/* Trust Badges */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-8 font-medium text-lg">As Featured On</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="bg-white px-6 py-4 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center gap-2">
                <svg viewBox="0 0 100 100" className="h-10 w-10" fill="none">
                  <circle cx="50" cy="50" r="45" fill="#FF6138"/>
                  <text x="50" y="65" textAnchor="middle" fill="white" fontSize="40" fontWeight="bold" fontFamily="Arial">A</text>
                </svg>
                <span className="text-2xl font-bold text-gray-800">angi</span>
              </div>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center gap-2">
                <svg viewBox="0 0 40 40" className="h-10 w-10">
                  <path d="M20 5 L35 20 L30 20 L30 35 L10 35 L10 20 L5 20 Z" fill="#F68B1F"/>
                  <rect x="16" y="22" width="8" height="13" fill="white"/>
                </svg>
                <span className="text-xl font-bold text-gray-800">HomeAdvisor</span>
              </div>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
              <span className="text-2xl font-serif font-bold text-red-600 tracking-tight italic">Dan's Papers</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
